// SPV Pool v1.0 PR 6 — Section D. Per-tranche distribution summary with a
// drill-down accordion that expands to the individual investor rows.
// Investor names link to the investor detail page.

import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Card } from "@/design-system/components/ui";
import { Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { formatCurrency } from "../../../shared/format";
import type { InvestorDistributionsDto } from "@/types/spvPool";

interface InvestorDistributionsCardProps {
  distributions: InvestorDistributionsDto;
}

export function InvestorDistributionsCard({
  distributions,
}: InvestorDistributionsCardProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const tranches = distributions.tranches;

  const toggle = (trancheId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(trancheId)) next.delete(trancheId);
      else next.add(trancheId);
      return next;
    });
  };

  return (
    <Card>
      <h3 className="font-display font-bold text-h3 m-0 mb-space-3">
        Investor Distributions
      </h3>
      {tranches.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No investor distributions for this period.
        </p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Tranche</TH>
              <TH># Distinct Investors Paid</TH>
              <TH>Total Net ($)</TH>
            </TR>
          </THead>
          <TBody>
            {tranches.map((t) => {
              const open = expanded.has(t.trancheId);
              return (
                <TrancheRows
                  key={t.trancheId}
                  open={open}
                  onToggle={() => toggle(t.trancheId)}
                  tranche={t}
                />
              );
            })}
          </TBody>
        </Table>
      )}
    </Card>
  );
}

function TrancheRows({
  open,
  onToggle,
  tranche,
}: {
  open: boolean;
  onToggle: () => void;
  tranche: InvestorDistributionsDto["tranches"][number];
}) {
  return (
    <>
      <TR>
        <TD>
          <button
            type="button"
            onClick={onToggle}
            className="flex items-center gap-space-2 text-left font-semibold"
            aria-expanded={open}
          >
            {open ? (
              <ChevronDown size={14} aria-hidden />
            ) : (
              <ChevronRight size={14} aria-hidden />
            )}
            <span>
              {tranche.trancheCode} — {tranche.trancheName}
            </span>
          </button>
        </TD>
        <TD>{tranche.distinctInvestorCount}</TD>
        <TD>{formatCurrency(tranche.totalNet)}</TD>
      </TR>
      {open && (
        <TR>
          <TD colSpan={3} className="bg-white-warm">
            {tranche.investors.length === 0 ? (
              <p className="text-small text-gray-mid italic m-0">
                No individual investor distributions in this tranche.
              </p>
            ) : (
              <ul className="m-0 p-0 list-none flex flex-col gap-space-1">
                {tranche.investors.map((inv) => (
                  <li
                    key={inv.investorId}
                    className="flex items-center justify-between text-small"
                  >
                    <Link
                      to={`/capital-markets/investors/${inv.investorId}`}
                      className="font-semibold hover:text-gold-shadow"
                    >
                      {inv.investorName}
                    </Link>
                    <span className="text-gray-mid">
                      {formatCurrency(inv.netAmount)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </TD>
        </TR>
      )}
    </>
  );
}
